import { Component, DestroyRef, computed, inject, signal, viewChild, TemplateRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { FeedbackFailComponent, TabItem, TabsComponent } from 'ok-angular-shared/components';
import { Organization } from 'src/app/features/organization/organization-models';
import { CustomerEntranceDialogData } from './customer-entrance-dialog.model';
import { AdmOrgTypeaheadComponent } from '../adm-org-typeahead/adm-org-typeahead.component';
import { translateOrganizationType } from '../../mappers/enum-translation-mapper';
import { createOrganizationActionItems } from 'src/app/features/organization/components/organization-list/organization-list.component';
import { OrganizationListDialogService } from 'src/app/features/organization/components/organization-list/organization-list-dialog.service';
import { OrganizationListStateService } from 'src/app/features/organization/components/organization-list/organization-list-state.service';
import { AuthService } from 'src/app/core/auth/auth.service';
import { EnvironmentLoaderService } from '../../services/environment-loader.service';
import OrganizationService from 'src/app/shared/services/organization.service';
import { TabDepartmentsComponent } from './tabs-content/tab-departments/tab-departments.component';
import { TabLocationsComponent } from './tabs-content/tab-locations/tab-locations.component';

@Component({
  selector: 'adm-customer-entrance-dialog',
  templateUrl: './customer-entrance-dialog.component.html',
  styleUrls: ['./customer-entrance-dialog.component.scss'],
  imports: [
    AdmOrgTypeaheadComponent,
    MatMenuModule,
    MatIconButton,
    MatIcon,
    MatMenuTrigger,
    MatTooltip,
    TabsComponent,
    TabDepartmentsComponent,
    TabLocationsComponent,
    FeedbackFailComponent,
  ],
  providers: [OrganizationListDialogService, OrganizationListStateService],
})
export class CustomerEntranceDialogComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject(MatDialogRef<CustomerEntranceDialogComponent>);
  private readonly data: CustomerEntranceDialogData = inject(MAT_DIALOG_DATA);
  private readonly dialogService = inject(OrganizationListDialogService);
  private readonly state = inject(OrganizationListStateService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly environmentLoaderService = inject(EnvironmentLoaderService);
  private readonly organizationService = inject(OrganizationService);
  private readonly hasDepartments = signal(false);
  private readonly typeahead = viewChild(AdmOrgTypeaheadComponent);
  private readonly navigateActions = new Set(['manageUsers', 'editAccessGroups']);

  public readonly departmentsLoaded = signal(false);
  public readonly translateOrganizationType = translateOrganizationType;
  public readonly org = signal(this.data.org);
  public readonly publicPortalName = this.environmentLoaderService.config.publicPortalName;
  public readonly typeaheadResetTrigger = signal<{ reset: boolean } | undefined>(undefined);
  public readonly departmentRef = viewChild<TemplateRef<unknown>>('departmentsContent');
  public readonly locationRef = viewChild<TemplateRef<unknown>>('locationsContent');
  public readonly tabs = computed<TabItem[]>(() => {
    const items: TabItem[] = [];
    const departmentRef = this.departmentRef();
    const locationRef = this.locationRef();

    if (this.hasDepartments() && departmentRef && locationRef) {
      items.push({ tabTitle: 'Afdelinger', tabContent: departmentRef, tabId: 'departments' });
      items.push({ tabTitle: 'Lokationer', tabContent: locationRef, tabId: 'locations' });
    }

    return items;
  });

  actionItems = createOrganizationActionItems({
    dialogService: this.dialogService,
    authService: this.authService,
    router: this.router,
    roles: this.environmentLoaderService.virtualRoles,
    publicPortalName: this.environmentLoaderService.config.publicPortalName,
  });

  menuItems = Object.entries(this.actionItems).map(([key, item]) => ({
    key,
    label: item.label,
    action: (org?: Organization) => {
      item.action(org);
      if (this.navigateActions.has(key)) {
        this.closeDialog();
      }
    },
    hiddenFn: item.hiddenFn,
    svgIcon: 'svgIcon' in item ? item.svgIcon : undefined,
    spriteIcon: 'icon' in item && typeof item.icon === 'object' ? item.icon.name : undefined,
    materialIcon: 'icon' in item && typeof item.icon === 'string' ? item.icon : undefined,
  }));

  constructor() {
    this.loadDepartmentsForOrganization(this.org());

    this.state.refreshSingleOrganization$
      .pipe(
        switchMap(organizationId => this.organizationService.getOrganization(organizationId)),
        tap(updatedOrg => {
          this.org.set(updatedOrg);
          this.loadDepartmentsForOrganization(updatedOrg);
          const typeahead = this.typeahead();
          typeahead?.refetch(typeahead.hasSelection() ? updatedOrg.id : undefined);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private loadDepartmentsForOrganization(org: Organization): void {
    this.departmentsLoaded.set(false);
    this.organizationService
      .getDepartmentsForOrganization(org.id, { active: '', direction: '' })
      .pipe(
        map(departments => departments.length > 0),
        catchError(() => of(false)),
        tap(hasDepartments => {
          this.hasDepartments.set(hasDepartments);
          this.departmentsLoaded.set(true);
        }),
        catchError(() => {
          this.hasDepartments.set(false);
          this.departmentsLoaded.set(true);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  onOrgSelected(org: Organization | null): void {
    if (org) {
      this.org.set(org);
      this.typeaheadResetTrigger.set({ reset: true });
      this.loadDepartmentsForOrganization(org);
    }
  }

  imitateOrganization(): void {
    this.actionItems.imitate.action(this.org());
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}
